/**
 * Browser precise-location capture for high-risk client actions.
 *
 * GBM-style hard gate: the client must share precise geolocation to request a
 * withdrawal or add/remove a bank account. The captured sample travels to the
 * backend in the `X-Geolocation` header, where it is recorded as an encrypted,
 * append-only attestation (migration 0029). Staff actions never call this.
 *
 * A denied or unavailable permission throws `GeolocationRequiredError` so the
 * calling UI can surface the hard-gate message and abort the action — matching
 * the server, which returns 428 when the header is absent.
 */

export interface GeoSample {
  lat: number;
  lng: number;
  accuracy_m: number | null;
  consent: boolean;
}

/** Thrown when the browser cannot or will not provide a precise location. */
export class GeolocationRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GeolocationRequiredError';
  }
}

// What the client reads when the browser will not give us a position. Same voice
// as the server's refusal: one sentence, their action, the one thing they can do.
// Never a status code, never why the control exists (rule of 2026-09-24).
const GATE_DENIED = 'Allow your browser to share your location, then try again.';
const GATE_UNAVAILABLE =
  'We could not get your location. Turn on location services, then try again.';

/** Prompt the browser for the current position. Rejects with a
 *  GeolocationRequiredError on denial, timeout, or lack of support. */
export function captureGeolocation(): Promise<GeoSample> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    return Promise.reject(
      new GeolocationRequiredError(
        'Your browser does not support location sharing, which is required to continue.',
      ),
    );
  }
  return new Promise<GeoSample>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
          consent: true,
        }),
      (err) =>
        reject(
          new GeolocationRequiredError(
            err.code === err.PERMISSION_DENIED ? GATE_DENIED : GATE_UNAVAILABLE,
          ),
        ),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

/** Capture location and return it as the `X-Geolocation` request header. */
export async function geolocationHeader(): Promise<Record<string, string>> {
  const sample = await captureGeolocation();
  return { 'X-Geolocation': JSON.stringify(sample) };
}

/** The same header for the routes where location is a SIGNAL, not a gate.
 *
 * Money leaving is gated: no location, no request (`geolocationHeader`, which
 * throws). Proving an email address or a phone number is not — the server
 * records the attestation when it arrives and scores the verification without
 * it when it does not, so a declined prompt must not stop someone proving
 * their own number. Returns `{}` instead of throwing.
 */
export async function optionalGeolocationHeader(): Promise<Record<string, string>> {
  try {
    return await geolocationHeader();
  } catch {
    return {};
  }
}
